import { DEFAULT_CURRENCY } from "@configs/currencies";
import type { CanvasVariant } from "./variant-resolver";

/**
 * Per-currency price selection for canvas variants.
 *
 * `variant.price` is the euro base; `variant.priceCurrencies` carries the
 * shelf prices the shop actually charges in PLN / CZK / RON / eur-at. The
 * rules here mirror src/utils/get-product-price.util.ts, whose shape
 * (actionPrice / actionPriceCurrencies) the variant does not have — variants
 * carry a single optional `compareAtPrice` in euros instead.
 */

/** What one price display needs: an amount and the currency it is in. */
export interface DisplayPrice {
  /** Integer minor units of `currency`. */
  amount: number;
  /** App currency code the amount is denominated in ("pln", "eur", "eur-at"). */
  currency: string;
  /** Was-price in the SAME denomination, or null when there is none to show. */
  compareAt: number | null;
}

/**
 * Presence, not truthiness. Zero is a real price — a `pln: 0` entry means
 * free in Poland, and swallowing it would quietly substitute the euro
 * amount, a number the customer would not be charged. A present numeric
 * entry is authoritative whatever its value; only a missing entry (or an
 * admin-cleared null, which is not a number at all) falls back to the euro
 * pair. This deliberately diverges from get-product-price.util.ts, whose
 * `> 0` rule exists so the store can FILTER products out — here nothing is
 * filtered, only displayed.
 */
const readCurrency = (
  map: Record<string, number> | undefined | null,
  currency: string,
): number | null => {
  if (!map) return null;
  const value = map[currency];
  return typeof value === "number" ? value : null;
};

/** "eur" and "eur-at" are both euros; only the market label differs. */
const isEuroDenominated = (currency: string) =>
  currency.split("-")[0] === DEFAULT_CURRENCY;

/**
 * The price to DISPLAY for one variant in one market currency.
 *
 * The amount and the currency travel as a pair, always. When the currency has
 * no entry in `priceCurrencies`, the fallback is the euro base formatted as
 * euros — never the euro number under the local symbol, which would show a
 * Polish customer "19,99 zł" for what is really an ~85 zł canvas.
 *
 * `compareAt` (the struck-through was-price) exists only as a euro figure on
 * the variant, so it survives only when the shown price is euro-denominated;
 * beside a złoty price it is suppressed rather than shown in the wrong
 * currency.
 */
export function variantDisplayPrice(
  variant: CanvasVariant,
  currency: string,
): DisplayPrice {
  const cur = (currency || DEFAULT_CURRENCY).toLowerCase();
  const compareAt =
    typeof variant.compareAtPrice === "number" ? variant.compareAtPrice : null;

  if (cur === DEFAULT_CURRENCY) {
    return { amount: variant.price, currency: DEFAULT_CURRENCY, compareAt };
  }

  const entry = readCurrency(variant.priceCurrencies, cur);
  if (entry !== null) {
    return {
      amount: entry,
      currency: cur,
      compareAt: isEuroDenominated(cur) ? compareAt : null,
    };
  }

  // No entry for this currency (including "eur-at and friends fall back to
  // the EUR figures", as get-product-price.util.ts puts it).
  return { amount: variant.price, currency: DEFAULT_CURRENCY, compareAt };
}

const activeVariants = (variants: CanvasVariant[]) =>
  (variants ?? []).filter((v) => v.active !== false);

/**
 * Do all active variants quote in one and the same currency?
 *
 * When they do not, their display amounts live on different scales and a
 * minimum across them is meaningless — so that (abnormal, half-seeded) case
 * falls back to the euro base, which every variant has.
 */
const singleDenomination = (active: CanvasVariant[], currency: string) =>
  new Set(active.map((v) => variantDisplayPrice(v, currency).currency)).size === 1;

/**
 * The cheapest active variant in the market's currency — the one the "from"
 * price actually refers to.
 *
 * The panel needs the variant and not just its price, because a bare "from
 * 19,90 €" says nothing about what you get for it; naming the size turns it
 * into an offer. Ties keep the first variant, matching the price reducer.
 */
export function cheapestVariant(
  variants: CanvasVariant[],
  currency: string,
): CanvasVariant | null {
  const active = activeVariants(variants);
  if (active.length === 0) return null;

  if (singleDenomination(active, currency)) {
    return active.reduce((min, v) =>
      variantDisplayPrice(v, currency).amount <
      variantDisplayPrice(min, currency).amount
        ? v
        : min,
    );
  }

  return active.reduce((min, v) => (v.price < min.price ? v : min));
}

/**
 * The "from" price for the upload panel: the cheapest active variant, in the
 * market's currency.
 *
 * Derived from the same pick as `cheapestVariant`, so the price and the size
 * shown beside it can never describe two different canvases. In the mixed
 * case the euro base is quoted without its compare-at, which is only
 * meaningful next to a euro-denominated price.
 */
export function cheapestDisplayPrice(
  variants: CanvasVariant[],
  currency: string,
): DisplayPrice | null {
  const active = activeVariants(variants);
  const pick = cheapestVariant(variants, currency);
  if (!pick) return null;

  if (singleDenomination(active, currency)) {
    return variantDisplayPrice(pick, currency);
  }
  return { amount: pick.price, currency: DEFAULT_CURRENCY, compareAt: null };
}

/**
 * Format minor units in the customer's locale.
 *
 * The region suffix is stripped before Intl sees the code: "eur-at" is this
 * app's market label, not ISO 4217, and `Intl.NumberFormat` throws a
 * RangeError on "EUR-AT" — the same mapping line-items-breakdown.tsx uses.
 */
export function formatMoney(
  cents: number,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale || "sl-SI", {
    style: "currency",
    currency: (currency || DEFAULT_CURRENCY).split("-")[0].toUpperCase(),
  }).format((cents ?? 0) / 100);
}
